import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Crown, Star, Check, Tag, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const SubscriptionRegistrationModal = ({ 
  isOpen, 
  onClose, 
  subscriptionTier, 
  subscriptionPlan,
  appliedPromoCode: initialPromoCode,
  onSubscriptionSuccess 
}) => {
  const [step, setStep] = useState('register'); // 'register', 'processing', 'redirecting'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Promo code state - internal to modal
  const [promoCode, setPromoCode] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [appliedPromoCode, setAppliedPromoCode] = useState(initialPromoCode || null);
  const [promoError, setPromoError] = useState('');
  
  const { login } = useAuth();
  const { toast } = useToast();

  // Update internal promo code if passed from parent
  useEffect(() => {
    if (initialPromoCode) {
      setAppliedPromoCode(initialPromoCode);
      setPromoCode(initialPromoCode.code || '');
    }
  }, [initialPromoCode]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep('register');
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: ''
      });
      setErrors({});
      if (!initialPromoCode) {
        setPromoCode('');
        setAppliedPromoCode(null);
        setPromoError('');
      }
    }
  }, [isOpen, initialPromoCode]);

  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoError('Please enter a promo code');
      return;
    }

    setPromoValidating(true);
    setPromoError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/validate-public`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: promoCode.trim().toUpperCase(),
          subscription_tier: subscriptionTier,
          amount: subscriptionPlan?.monthly_price || 0
        })
      });

      const result = await response.json();

      if (result.valid) {
        setAppliedPromoCode({
          code: promoCode.trim().toUpperCase(),
          ...result
        });
        toast({
          title: "Promo Code Applied!",
          description: result.message
        });
      } else {
        setPromoError(result.message);
        setAppliedPromoCode(null);
      }
    } catch (error) {
      console.error('Promo validation error:', error);
      setPromoError('Failed to validate promo code');
    } finally {
      setPromoValidating(false);
    }
  };

  const clearPromoCode = () => {
    setPromoCode('');
    setAppliedPromoCode(null);
    setPromoError('');
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setStep('processing');

    try {
      // Step 1: Register the user
      const registrationResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          full_name: `${formData.firstName} ${formData.lastName}`.trim()
        }),
      });

      if (!registrationResponse.ok) {
        const errorData = await registrationResponse.json();
        throw new Error(errorData.detail || 'Registration failed');
      }

      // Step 2: Login the user to get token
      const loginResult = await login({
        email: formData.email,
        password: formData.password
      });

      if (!loginResult.success) {
        throw new Error('Login after registration failed');
      }

      toast({
        title: "Account Created Successfully!",
        description: subscriptionTier === 'free' ? "Welcome to TribalRoot.AI!" : "Redirecting you to secure payment...",
      });

      // Step 3: For Free tier, just complete registration and redirect to dashboard
      if (subscriptionTier === 'free') {
        // Free tier - no payment needed
        setTimeout(() => {
          onClose();
          window.location.href = '/tribal-vault'; // Redirect to a page free users can access
        }, 1500);
        return;
      }

      // Step 4: For paid tiers (Seekers, Crown), create checkout session
      setStep('redirecting');
      
      let checkoutResponse;
      
      // If promo code is applied, use the coupon checkout endpoint
      if (appliedPromoCode && appliedPromoCode.valid) {
        checkoutResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/apply-to-checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({
            code: appliedPromoCode.code,
            subscription_tier: subscriptionTier,
            billing_interval: 'monthly',
            origin_url: window.location.origin
          })
        });
      } else {
        // Regular checkout without promo code
        checkoutResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({
            subscription_tier: subscriptionTier,
            billing_interval: 'monthly',
            origin_url: window.location.origin
          })
        });
      }

      if (!checkoutResponse.ok) {
        const errorData = await checkoutResponse.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const checkoutData = await checkoutResponse.json();
      
      if (checkoutData.checkout_url) {
        // Close modal and redirect to Stripe
        onClose();
        window.location.href = checkoutData.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }

    } catch (error) {
      console.error('Registration + subscription error:', error);
      setStep('register');
      setIsSubmitting(false);
      
      toast({
        variant: "destructive",
        title: "Registration Failed",
        description: error.message || "Failed to create account and start subscription. Please try again.",
      });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const getTierIcon = () => {
    return subscriptionTier === 'crown' ? <Crown className="w-6 h-6" /> : <Star className="w-6 h-6" />;
  };

  const getTierColor = () => {
    return subscriptionTier === 'crown' ? 'text-amber-600' : 'text-blue-600';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-center text-xl">
            {step === 'register' && (
              <>
                <div className={`mr-2 ${getTierColor()}`}>
                  {getTierIcon()}
                </div>
                Subscribe to {subscriptionPlan?.name}
              </>
            )}
            {step === 'processing' && 'Creating Your Account...'}
            {step === 'redirecting' && 'Redirecting to Payment...'}
          </DialogTitle>
        </DialogHeader>

        {step === 'register' && (
          <div className="space-y-4">
            {/* Plan Summary */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-900">{subscriptionPlan?.name}</span>
                {appliedPromoCode && appliedPromoCode.valid ? (
                  <div className="text-right">
                    <span className="text-sm text-gray-400 line-through mr-2">
                      ${subscriptionPlan?.monthly_price}
                    </span>
                    <span className="text-lg font-bold text-green-600">
                      ${appliedPromoCode.final_amount?.toFixed(2)}/month
                    </span>
                  </div>
                ) : (
                  <span className="text-lg font-bold text-green-600">
                    ${subscriptionPlan?.monthly_price}/month
                  </span>
                )}
              </div>
              
              {/* Promo Code Input Section */}
              {subscriptionTier !== 'free' && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Tag className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-gray-700">Have a promo code?</span>
                  </div>
                  
                  {appliedPromoCode && appliedPromoCode.valid ? (
                    // Show applied promo code with option to remove
                    <div className="flex items-center justify-between bg-green-100 text-green-700 px-3 py-2 rounded-md text-sm">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        <span className="font-semibold">{appliedPromoCode.code}</span>
                        <span>-</span>
                        <span>
                          {appliedPromoCode.discount_type === 'percentage' 
                            ? `${appliedPromoCode.discount_value}% off`
                            : `$${appliedPromoCode.discount_value} off`}
                        </span>
                      </div>
                      <Button 
                        type="button"
                        variant="ghost" 
                        size="sm"
                        onClick={clearPromoCode}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    // Show promo code input
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter code"
                        value={promoCode}
                        onChange={(e) => {
                          setPromoCode(e.target.value.toUpperCase());
                          setPromoError('');
                        }}
                        className="flex-1 h-9"
                        disabled={promoValidating}
                        data-testid="modal-promo-code-input"
                      />
                      <Button 
                        type="button"
                        onClick={validatePromoCode}
                        disabled={promoValidating || !promoCode.trim()}
                        className="bg-green-600 hover:bg-green-700 h-9 px-3"
                        data-testid="modal-apply-promo-btn"
                      >
                        {promoValidating ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Apply'
                        )}
                      </Button>
                    </div>
                  )}
                  
                  {promoError && (
                    <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                      <XCircle className="w-3 h-3" />
                      {promoError}
                    </div>
                  )}
                </div>
              )}
              
              {subscriptionTier === 'crown' && subscriptionPlan?.activation_fee > 0 && (
                <div className="text-sm text-amber-600 mt-2">
                  + ${subscriptionPlan.activation_fee} initial setup fee
                </div>
              )}
              <div className="mt-2">
                <div className="flex items-center text-sm text-gray-600">
                  <Check className="w-3 h-3 mr-1 text-green-600" />
                  Full access to all premium features
                </div>
              </div>
            </div>

            {/* Registration Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className={errors.firstName ? 'border-red-500' : ''}
                    placeholder="Enter your first name"
                  />
                  {errors.firstName && (
                    <span className="text-red-500 text-xs">{errors.firstName}</span>
                  )}
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={errors.lastName ? 'border-red-500' : ''}
                    placeholder="Enter your last name"
                  />
                  {errors.lastName && (
                    <span className="text-red-500 text-xs">{errors.lastName}</span>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={errors.email ? 'border-red-500' : ''}
                  placeholder="Enter your email"
                />
                {errors.email && (
                  <span className="text-red-500 text-xs">{errors.email}</span>
                )}
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className={errors.password ? 'border-red-500' : ''}
                  placeholder="Choose a secure password"
                />
                {errors.password && (
                  <span className="text-red-500 text-xs">{errors.password}</span>
                )}
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  className={errors.confirmPassword ? 'border-red-500' : ''}
                  placeholder="Confirm your password"
                />
                {errors.confirmPassword && (
                  <span className="text-red-500 text-xs">{errors.confirmPassword}</span>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="flex-1"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className={`flex-1 ${
                    subscriptionTier === 'crown' 
                      ? 'bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800'
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                  } text-white`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    'Create Account & Subscribe'
                  )}
                </Button>
              </div>
            </form>

            <div className="text-center">
              <p className="text-xs text-gray-500">
                By subscribing, you agree to our Terms of Service and Privacy Policy
              </p>
            </div>
          </div>
        )}

        {(step === 'processing' || step === 'redirecting') && (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">
                {step === 'processing' ? 'Creating Your Account...' : 'Redirecting to Secure Payment...'}
              </h3>
              <p className="text-sm text-gray-600">
                {step === 'processing' 
                  ? 'Setting up your TribalRoot.AI account with premium access'
                  : 'Taking you to Stripe for secure payment processing'
                }
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionRegistrationModal;